import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  FileText,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Clock,
  Loader,
  TrendingUp
} from 'lucide-react';
import { blotterService, Blotter } from '../../services/blotter';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/currencyUtils';
import BlotterTransactionList from './BlotterTransactionList';

const BlotterDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [blotter, setBlotter] = useState<Blotter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchBlotter(parseInt(id));
    }
  }, [id]);

  const fetchBlotter = async (blotterId: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await blotterService.getBlotter(blotterId);
      setBlotter(response.data || null);
    } catch (err: any) {
      console.error('Failed to fetch blotter:', err);
      setError(err.response?.data?.message || 'Failed to load blotter details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
      completed: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: <CheckCircle className="w-4 h-4" />
      },
      processing: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        icon: <Loader className="w-4 h-4 animate-spin" />
      },
      pending: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        icon: <Clock className="w-4 h-4" />
      },
      error: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        icon: <AlertCircle className="w-4 h-4" />
      }
    };

    const badge = badges[status] || badges.pending;

    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading blotter details...</p>
        </div>
      </div>
    );
  }

  if (error || !blotter) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/csv-upload')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blotters
          </button>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <div>
                <h3 className="text-lg font-semibold text-red-900">Error Loading Blotter</h3>
                <p className="text-red-700 mt-1">{error || 'Blotter not found'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb & Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/csv-upload')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blotters
          </button>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Home</span>
            <span>/</span>
            <span>CSV Upload</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">{formatDate(blotter.date)}</span>
          </div>
        </div>

        {/* Blotter Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Blotter Details
              </h1>
              <p className="text-gray-600">
                {formatDate(blotter.date)}
              </p>
            </div>
            <div>
              {getStatusBadge(blotter.status)}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Filename</p>
                <p className="font-medium text-gray-900">{blotter.original_filename || blotter.filename}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-50 rounded-lg">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Uploaded</p>
                <p className="font-medium text-gray-900">{formatDate(blotter.created_at)}</p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {blotter.status === 'error' && blotter.error_message && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-800 font-medium mb-1">
                <AlertCircle className="w-4 h-4" />
                Error Details
              </div>
              <p className="text-sm text-red-700">{blotter.error_message}</p>
            </div>
          )}
        </div>

        {/* Statistics Cards */}
        {blotter.status === 'completed' && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-gray-600">Transactions</div>
                <FileText className="w-5 h-5 text-gray-400" />
              </div>
              <div className="text-3xl font-bold text-gray-900">
                {blotter.total_trades || 0}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-gray-600">Total Commission</div>
                <DollarSign className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-3xl font-bold text-gray-900">
                {formatCurrency(blotter.total_commission || 0)}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-gray-600">Salesman Payout</div>
                <TrendingUp className="w-5 h-5 text-blue-500" />
              </div>
              <div className="text-3xl font-bold text-blue-600">
                {formatCurrency(blotter.payout_commission || 0)}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {blotter.total_commission > 0
                  ? ((Number(blotter.payout_commission) / Number(blotter.total_commission)) * 100).toFixed(1)
                  : 0}% paid out (excludes House)
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-gray-600">House Commission</div>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-3xl font-bold text-green-600">
                {formatCurrency(blotter.net_commission || 0)}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                House keeps
              </div>
            </div>
          </div>
        )}

        {/* Transactions List */}
        {blotter.status === 'completed' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Transactions</h2>
              <p className="text-sm text-gray-600 mt-1">
                All transactions from this blotter
              </p>
            </div>
            <BlotterTransactionList blotterId={parseInt(id!)} />
          </div>
        )}

        {/* Processing/Pending State */}
        {(blotter.status === 'processing' || blotter.status === 'pending') && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <Loader className="w-6 h-6 text-blue-600 animate-spin" />
              <div>
                <h3 className="text-lg font-semibold text-blue-900">
                  {blotter.status === 'processing' ? 'Processing Blotter...' : 'Blotter Pending'}
                </h3>
                <p className="text-blue-700 mt-1">
                  Transactions will be available once processing is complete.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlotterDetail;
